import { useNavigate, useLocation } from 'react-router-dom';
import { LogOut } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { getEmail, logout } from '@/auth/admin-auth';
import { adminReadOnlyLabel, isReadOnlyAdminMode } from '@/lib/admin-mode';
import { cn } from '@/lib/utils';

type EnvLabel = 'PROD' | 'DEV' | 'LOCAL';

function readEnvLabel(): EnvLabel {
  const raw = (import.meta.env.VITE_ENV ?? 'local').toLowerCase();
  if (raw === 'production' || raw === 'prod') return 'PROD';
  if (raw === 'dev' || raw === 'develop' || raw === 'staging') return 'DEV';
  return 'LOCAL';
}

const ENV_STYLES: Record<EnvLabel, string> = {
  PROD: 'bg-red-100 text-red-700 border-red-200',
  DEV: 'bg-amber-100 text-amber-700 border-amber-200',
  LOCAL: 'bg-zinc-100 text-zinc-700 border-zinc-200',
};

const TITLE_BY_ROUTE: Record<string, string> = {
  '/acquisition': 'Acquisition',
  '/growth': 'Acquisition',
  '/overview': 'Acquisition',
  '/competitions': 'Competitions',
  '/people-and-agents': 'People & Agents',
  '/users-and-agents': 'Users & Agents',
  '/feedback': 'Feedback',
  '/creators': 'Creators',
  '/game-data': 'Game Data',
  '/content': 'Game Data',
  '/elevenlabs': 'ElevenLabs',
  '/ai': 'ElevenLabs',
  '/system': 'System',
};

export function Topbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const env = readEnvLabel();
  const readOnly = isReadOnlyAdminMode();
  const email = getEmail();

  const title = TITLE_BY_ROUTE[location.pathname] ?? 'Admin';

  function handleLogout() {
    logout();
    navigate('/login', { replace: true });
  }

  return (
    <header className="flex min-h-16 shrink-0 flex-wrap items-center justify-between gap-2 border-b bg-background px-4 py-3 md:h-16 md:flex-nowrap md:px-6 md:py-0">
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <span>Admin</span>
        <span className="text-muted-foreground/40">/</span>
        <span className="font-medium text-foreground">{title}</span>
      </div>

      <div className="flex items-center gap-3">
        <Badge variant="outline" className={cn('font-mono text-[11px]', ENV_STYLES[env])}>
          {env}
        </Badge>
        {readOnly ? (
          <Badge variant="outline" className="border-red-200 bg-red-50 font-mono text-[11px] text-red-700">
            {adminReadOnlyLabel()}
          </Badge>
        ) : null}
        {email ? (
          <>
            <Separator orientation="vertical" className="hidden h-6 lg:block" />
            <span className="hidden text-sm text-muted-foreground lg:inline">{email}</span>
          </>
        ) : null}
        <Button variant="ghost" size="sm" onClick={handleLogout}>
          <LogOut className="h-4 w-4" />
          Log out
        </Button>
      </div>
    </header>
  );
}
